import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, Sparkles, Loader2, X, Check, ChevronDown } from "lucide-react";
import { PageShell } from "@/components/Navbar";
import { Fade } from "@/components/proptech";
import { api, apiError } from "@/lib/api";
import { readReferralResume, saveReferralResume } from "@/lib/referralResume";
import { useAuth } from "@/context/AuthContext";
import { ServiceAreaPicker } from "@/components/ServiceAreaPicker";
import { PostPublishDistribution } from "@/components/PostPublishDistribution";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/currency-input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectGroup, SelectLabel, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const COMPLETION_OPTIONS = ["Pre-selling", "Under Construction", "Ready for Occupancy (RFO)", "Resale / Existing"];
import { AMENITIES, normalizeAmenities, PROPERTY_TYPE_GROUPS, propertyTypeLabel } from "@/lib/taxonomy";
const NONE = "__none__";
const PSGC_CODE = /^\d{10}$/;

/** Small Required / Preferred (Flexible) segmented toggle. `flexible=false` => Required. */
const ReqToggle = ({ flexible, onChange, testid, flexibleLabel = "Preferred / Flexible" }) => (
  <div className="inline-flex rounded-lg border border-border overflow-hidden text-xs font-semibold" data-testid={testid}>
    <button type="button" data-testid={`${testid}-required`} onClick={() => onChange(false)}
      className={`px-3 py-1.5 transition-colors ${!flexible ? "bg-primary text-primary-foreground" : "bg-white text-slate-500 hover:bg-slate-50"}`}>Required</button>
    <button type="button" data-testid={`${testid}-flexible`} onClick={() => onChange(true)}
      className={`px-3 py-1.5 transition-colors ${flexible ? "bg-accent text-accent-foreground" : "bg-white text-slate-500 hover:bg-slate-50"}`}>{flexibleLabel}</button>
  </div>
);

const chipsFromNames = (names = [], codes = []) =>
  names.map((name, i) => ({ name, level: "CITY_MUNICIPALITY", code: codes[i] && PSGC_CODE.test(codes[i]) ? codes[i] : `legacy:${name}` }));

export default function HaveBuyer() {
  const nav = useNavigate();
  const { user } = useAuth();
  const canPublish = !!user && (user.role === "ADMIN" || (user.account_status === "ACTIVE" && user.verification_status === "PROFESSIONAL_VERIFIED"));
  const [sp] = useSearchParams();
  const editId = sp.get("edit") || "";
  const [saving, setSaving] = useState(false);
  const [draftId, setDraftId] = useState(sp.get("edit") || null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [distrib, setDistrib] = useState(null); // post-publish distribution target
  const [loadingRec, setLoadingRec] = useState(!!editId);
  const [showAdvanced, setShowAdvanced] = useState(!!editId);
  const [detected, setDetected] = useState([]);
  const [smartPrefs, setSmartPrefs] = useState([]);
  const [detecting, setDetecting] = useState(false);
  const [f, setF] = useState({
    buyer_alias: "", property_type: "Condominium", listing_type: "For Sale",
    preferred: [], nearby: [], neighborhoods: "",
    location_flexible: true, property_type_flexible: true,
    budget_min: "", budget_max: "", budget_flexible: false, budget_tolerance_pct: "10",
    min_bedrooms: "1", max_bedrooms: "", bedrooms_flexible: true,
    min_bathrooms: "0", floor_area_min: "", floor_area_max: "", lot_area_min: "", lot_area_max: "",
    parking_required: false, parking_min: "0", completion_pref: "", completion_flexible: true,
    furnishing_pref: "Any", required_features: [], preferred_features: [], notes: "",
    visibility: "PUBLIC_MARKETPLACE", public_description: "",
    stay_check_in: "", stay_check_out: "", stay_guests: "2",
    nightly_budget_min: "", nightly_budget_max: "", stay_beds_min: "",
  });
  const isStay = f.listing_type === "Unit Hosting";
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleFeat = (key, v) => setF((p) => ({ ...p, [key]: p[key].includes(v) ? p[key].filter((x) => x !== v) : [...p[key], v] }));

  // Fresh-form visibility default is Public Marketplace; downgrade to Network for
  // pros who can't publish (that option is disabled for them). Edit-load is untouched.
  useEffect(() => {
    if (!editId && !canPublish) {
      setF((p) => (p.visibility === "PUBLIC_MARKETPLACE" ? { ...p, visibility: "NETWORK_ONLY" } : p));
    }
  }, [canPublish, editId]);

  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data: d } = await api.get(`/buyer-requirements/${editId}`);
        // Legacy-safe toggle init: derive from stored engine flags if present, else legacy defaults
        // (all criteria SOFT except budget which keeps its approved legacy HARD behavior).
        const flex = (flexKey, mandKey, dflt) =>
          d[flexKey] !== undefined ? d[flexKey] : (d[mandKey] !== undefined ? !d[mandKey] : dflt);
        setF({
          buyer_alias: d.buyer_alias || "", property_type: d.property_type || "Condominium",
          listing_type: d.listing_type || "For Sale",
          preferred: chipsFromNames(d.cities || [], d.location_codes || []),
          nearby: chipsFromNames(d.nearby_cities || [], []),
          neighborhoods: (d.neighborhoods || []).join(", "),
          location_flexible: flex("location_flexible", "location_mandatory", true),
          property_type_flexible: flex("property_type_flexible", "property_type_mandatory", true),
          budget_min: d.budget_min ? String(d.budget_min) : "", budget_max: d.budget_max ? String(d.budget_max) : "",
          budget_flexible: d.budget_flexible !== undefined ? d.budget_flexible : (d.budget_soft !== undefined ? d.budget_soft : false),
          budget_tolerance_pct: String(d.budget_tolerance_pct ?? 10),
          min_bedrooms: String(d.min_bedrooms ?? 0), max_bedrooms: d.max_bedrooms ? String(d.max_bedrooms) : "",
          bedrooms_flexible: flex("bedrooms_flexible", "bedrooms_mandatory", true),
          min_bathrooms: String(d.min_bathrooms ?? 0),
          floor_area_min: d.floor_area_min ? String(d.floor_area_min) : "", floor_area_max: d.floor_area_max ? String(d.floor_area_max) : "",
          lot_area_min: d.lot_area_min ? String(d.lot_area_min) : "", lot_area_max: d.lot_area_max ? String(d.lot_area_max) : "",
          parking_required: !!d.parking_required, parking_min: String(d.parking_min ?? 0),
          completion_pref: d.completion_pref || "", completion_flexible: flex("completion_flexible", "completion_mandatory", true),
          furnishing_pref: d.furnishing_pref || "Any",
          required_features: normalizeAmenities(d.required_features || []),
          preferred_features: normalizeAmenities(d.preferred_features && d.preferred_features.length ? d.preferred_features : (d.must_haves || [])),
          notes: d.notes || "",
          visibility: d.visibility || "NETWORK_ONLY", public_description: d.public_description || "",
          stay_check_in: d.stay_check_in || "", stay_check_out: d.stay_check_out || "",
          stay_guests: String(d.stay_guests ?? 2),
          nightly_budget_min: d.nightly_budget_min ? String(d.nightly_budget_min) : "",
          nightly_budget_max: d.nightly_budget_max ? String(d.nightly_budget_max) : "",
          stay_beds_min: d.stay_beds_min ? String(d.stay_beds_min) : "",
        });
        setSmartPrefs(d.semantic_preferences || []);
      } catch (e) { toast.error(apiError(e)); nav("/my-buyers"); }
      finally { setLoadingRec(false); }
    })();
  }, [editId]);

  const num = (v) => (v === "" ? null : parseInt(v));
  const numF = (v) => (v === "" ? null : parseFloat(v));  // P0-C: decimal areas

  const validate = () => {
    const e = {};
    const bmin = num(f.budget_min), bmax = num(f.budget_max);
    if (bmin != null && bmax != null && bmin > bmax) e.budget = "Budget min must be ≤ max.";
    const brMin = num(f.min_bedrooms), brMax = num(f.max_bedrooms);
    if (brMin != null && brMax != null && brMin > brMax) e.bedrooms = "Min bedrooms must be ≤ max.";
    const faMin = numF(f.floor_area_min), faMax = numF(f.floor_area_max);
    if (faMin != null && faMax != null && faMin > faMax) e.floor = "Floor-area min must be ≤ max.";
    const laMin = numF(f.lot_area_min), laMax = numF(f.lot_area_max);
    if (laMin != null && laMax != null && laMin > laMax) e.lot = "Lot-area min must be ≤ max.";
    const tol = num(f.budget_tolerance_pct);
    if (f.budget_flexible && tol != null && (tol < 0 || tol > 50)) e.tol = "Tolerance must be between 0 and 50%.";
    const pk = num(f.parking_min);
    if (pk != null && pk < 0) e.parking = "Parking slots must be ≥ 0.";
    if (isStay) {
      const nlo = num(f.nightly_budget_min), nhi = num(f.nightly_budget_max);
      if (nlo != null && nhi != null && nlo > nhi) e.nightly = "Nightly budget min must be ≤ max.";
      if (f.stay_check_in && f.stay_check_out && f.stay_check_out <= f.stay_check_in) e.stay_dates = "Check-out must be after check-in.";
    }
    return e;
  };
  const errors = validate();

  const detect = async () => {
    if (!f.notes.trim()) { toast.error("Add some notes first."); return; }
    setDetecting(true);
    try {
      const { data } = await api.post("/ai/extract-preferences", { note: f.notes });
      setDetected(data.preferences);
      if (!data.preferences.length) toast("No soft preferences detected in the note.");
    } catch (e) { toast.error(apiError(e)); } finally { setDetecting(false); }
  };

  const buildBuyerPayload = () => {
      const cities = f.preferred.map((p) => p.name);
      const location_codes = f.preferred.filter((p) => PSGC_CODE.test(p.code)).map((p) => p.code);
      const payload = {
        buyer_alias: f.buyer_alias, property_type: f.property_type, listing_type: f.listing_type,
        cities, location_codes, nearby_cities: f.nearby.map((n) => n.name),
        neighborhoods: f.neighborhoods ? f.neighborhoods.split(",").map((s) => s.trim()).filter(Boolean) : [],
        location_flexible: f.location_flexible, property_type_flexible: f.property_type_flexible,
        budget_min: parseInt(f.budget_min || 0), budget_max: parseInt(f.budget_max || 0),
        budget_flexible: f.budget_flexible, budget_tolerance_pct: parseInt(f.budget_tolerance_pct || 10),
        min_bedrooms: parseInt(f.min_bedrooms || 0), max_bedrooms: num(f.max_bedrooms), bedrooms_flexible: f.bedrooms_flexible,
        min_bathrooms: parseInt(f.min_bathrooms || 0),
        floor_area_min: numF(f.floor_area_min), floor_area_max: numF(f.floor_area_max),
        lot_area_min: numF(f.lot_area_min), lot_area_max: numF(f.lot_area_max),
        parking_required: f.parking_required, parking_min: parseInt(f.parking_min || 0),
        completion_pref: f.completion_pref, completion_flexible: f.completion_flexible,
        furnishing_pref: f.furnishing_pref,
        required_features: f.required_features, preferred_features: f.preferred_features,
        must_haves: f.preferred_features, semantic_preferences: smartPrefs,
        notes: f.notes,
        visibility: f.visibility, public_description: f.public_description,
        stay_check_in: isStay ? f.stay_check_in : "", stay_check_out: isStay ? f.stay_check_out : "",
        stay_guests: isStay ? parseInt(f.stay_guests || 0) : 0,
        nightly_budget_min: isStay ? parseInt(f.nightly_budget_min || 0) : 0,
        nightly_budget_max: isStay ? parseInt(f.nightly_budget_max || 0) : 0,
        stay_beds_min: isStay ? parseInt(f.stay_beds_min || 0) : 0,
      };
      if (isStay) { payload.budget_min = 0; payload.budget_max = 0; }
      return payload;
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/buyer-requirements/draft", { ...buildBuyerPayload(), id: draftId || null });
      setDraftId(data.id);
      setDraftSavedAt(data.updated_at || new Date().toISOString());
      toast.success("Draft saved");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingDraft(false); }
  };

  const submit = async () => {
    if (!f.buyer_alias) { toast.error("Add a buyer label."); return; }
    if (f.preferred.length === 0) { toast.error("Add at least one preferred location."); return; }
    if (Object.keys(errors).length) { toast.error("Fix the highlighted fields first."); setShowAdvanced(true); return; }
    setSaving(true);
    try {
      const payload = buildBuyerPayload();
      const publishId = editId || draftId;  // publishing a continued draft activates it
      if (publishId) {
        const { data } = await api.put(`/buyer-requirements/${publishId}`, payload);
        toast.success("Buyer Need updated");
        nav(`/buyer/${data.id}/matches`);
      } else {
        const { data } = await api.post("/buyer-requirements", payload);
        // CP5: if we arrived here from the Refer-a-Client flow, resume it with this new
        // Buyer Need selected and the original recipient preserved.
        if (sp.get("resume") === "referral") {
          const r = readReferralResume();
          if (r) { saveReferralResume({ ...r, needId: data.id }); toast.success("Buyer Need created — back to referral"); nav(r.returnTo || "/connections"); return; }
        }
        toast.success("Buyer saved — finding properties…");
        setSaving(false);
        setDistrib({ id: data.id });
      }
    } catch (e) { toast.error(apiError(e)); setSaving(false); }
  };

  if (loadingRec) return <PageShell testid="have-buyer-page"><div className="flex items-center gap-2 text-slate-500 py-20 justify-center"><Loader2 className="h-5 w-5 animate-spin" /> Loading…</div></PageShell>;

  const Err = ({ id }) => errors[id] ? <p className="text-xs text-red-600 mt-1" data-testid={`err-${id}`}>{errors[id]}</p> : null;

  return (
    <PageShell testid="have-buyer-page">
      <Fade>
        <div className="mb-6">
          <p className="eyebrow flex items-center gap-2"><UserPlus className="h-4 w-4" /> {editId ? "Edit Buyer Need" : "I Have a Buyer"}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">{editId ? "Update your Buyer Need" : "Tell us what your buyer needs"}</h1>
          <p className="text-slate-500 mt-1">Mark each criterion <span className="font-semibold text-primary">Required</span> or <span className="font-semibold text-accent-foreground">Preferred / Flexible</span>. We match it against every active listing in the network.</p>
        </div>
      </Fade>
      <Fade delay={0.05}>
        <div className="bg-slate-50 border border-border rounded-2xl p-6 sm:p-8 grid md:grid-cols-2 gap-5 max-w-4xl">
          {/* ---- PRIMARY ---- */}
          <div className="md:col-span-2"><Label>Buyer label / alias</Label><Input data-testid="input-buyer-alias" value={f.buyer_alias} onChange={(e) => set("buyer_alias", e.target.value)} placeholder="e.g. OFW family relocating" className="mt-1.5" /></div>

          {/* P1: Buyer Need visibility */}
          <div className="md:col-span-2 rounded-xl border border-border bg-white p-4" data-testid="buyer-visibility-block">
            <Label className="text-sm font-semibold">Buyer Need visibility</Label>
            <p className="text-xs text-slate-500 mt-1">Choose where this buyer need appears. The buyer's identity and contact details always stay private.</p>
            <div className="grid sm:grid-cols-3 gap-2 mt-3">
              {[
                { v: "PUBLIC_MARKETPLACE", t: "Public Marketplace", d: "Discoverable by anyone on the public marketplace. Buyer stays anonymous." },
                { v: "NETWORK_ONLY", t: "PropertyConnect Network", d: "Visible to signed-in PropertyConnect professionals only." },
                { v: "PRIVATE", t: "Private", d: "Only you can see and manage this buyer need." },
              ].map((o) => {
                const disabled = o.v === "PUBLIC_MARKETPLACE" && !canPublish;
                const active = f.visibility === o.v;
                return (
                  <button key={o.v} type="button" data-testid={`visibility-${o.v}`} disabled={disabled}
                    onClick={() => set("visibility", o.v)}
                    className={`text-left rounded-lg border p-3 transition-colors ${active ? "border-primary bg-primary/5 ring-1 ring-primary" : "border-border bg-white hover:border-slate-400"} ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}>
                    <span className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">{active && <Check className="h-3.5 w-3.5 text-primary" />}{o.t}</span>
                    <span className="block text-[11px] text-slate-500 mt-1">{o.d}</span>
                  </button>
                );
              })}
            </div>
            {!canPublish && <p className="text-[11px] text-amber-600 mt-2" data-testid="publish-gate-note">Public Marketplace publishing unlocks once your professional profile is verified.</p>}
            {f.visibility === "PUBLIC_MARKETPLACE" && (
              <div className="mt-3">
                <Label>Public description <span className="text-slate-400 font-normal">(optional, shown publicly)</span></Label>
                <Textarea data-testid="input-public-description" value={f.public_description} onChange={(e) => set("public_description", e.target.value)} placeholder="Describe the Buyer Need for the public marketplace (e.g. Growing family seeking a 3BR near BGC schools)." className="mt-1.5" />
                <p className="text-[11px] text-amber-600 mt-1">Do not enter the buyer's name, contact details, or any identifying information here. The private Notes field is never published.</p>
              </div>
            )}
          </div>

          <div>
            <Label>Transaction</Label>
            <Select value={f.listing_type} onValueChange={(v) => set("listing_type", v)}><SelectTrigger data-testid="select-listing-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{[["For Sale", "For Sale"], ["For Rent", "For Rent"], ["Unit Hosting", "Stays"]].map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
          </div>
          {isStay && (
            <div className="md:col-span-2 grid md:grid-cols-2 gap-5 rounded-xl border border-emerald-600/40 bg-emerald-50/40 p-5" data-testid="stay-need-fields">
              <div className="md:col-span-2"><p className="text-sm font-semibold text-emerald-800 flex items-center gap-2">Stay requirement details</p></div>
              <div><Label>Check-in date</Label><Input data-testid="input-stay-checkin" type="date" value={f.stay_check_in} onChange={(e) => set("stay_check_in", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Check-out date</Label><Input data-testid="input-stay-checkout" type="date" value={f.stay_check_out} onChange={(e) => set("stay_check_out", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Guests</Label><Input data-testid="input-stay-guests" type="number" min="1" value={f.stay_guests} onChange={(e) => set("stay_guests", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Min beds</Label><Input data-testid="input-stay-beds" type="number" min="0" value={f.stay_beds_min} onChange={(e) => set("stay_beds_min", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Nightly budget min (₱)</Label><CurrencyInput data-testid="input-nightly-budget-min" value={f.nightly_budget_min} onValueChange={(v) => set("nightly_budget_min", v)} placeholder="Min / night" className="mt-1.5" /></div>
              <div><Label>Nightly budget max (₱)</Label><CurrencyInput data-testid="input-nightly-budget-max" value={f.nightly_budget_max} onValueChange={(v) => set("nightly_budget_max", v)} placeholder="Max / night" className="mt-1.5" /></div>
              <div className="md:col-span-2 space-y-1"><Err id="nightly" /><Err id="stay_dates" /></div>
            </div>
          )}
          <div>
            <div className="flex items-center justify-between"><Label>Property type</Label><ReqToggle testid="toggle-property-type" flexible={f.property_type_flexible} onChange={(v) => set("property_type_flexible", v)} /></div>
            <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}><SelectTrigger data-testid="select-property-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Any">Any / Flexible</SelectItem>{PROPERTY_TYPE_GROUPS.map((g) => (<SelectGroup key={g.label}><SelectLabel>{g.label}</SelectLabel>{g.items.map((t) => <SelectItem key={t} value={t}>{propertyTypeLabel(t)}</SelectItem>)}</SelectGroup>))}</SelectContent></Select>
          </div>
          <div className="md:col-span-2">
            <div className="flex items-center justify-between"><Label>Preferred location(s)</Label><ReqToggle testid="toggle-location" flexible={f.location_flexible} onChange={(v) => set("location_flexible", v)} /></div>
            <ServiceAreaPicker testid="buyer-location-picker" value={f.preferred} onChange={(v) => set("preferred", v)} />
          </div>
          <div className={isStay ? "hidden" : ""}>
            <div className="flex items-center justify-between"><Label>Budget (₱)</Label><ReqToggle testid="toggle-budget" flexible={f.budget_flexible} onChange={(v) => set("budget_flexible", v)} flexibleLabel="Flexible" /></div>
            <div className="grid grid-cols-2 gap-2 mt-1.5">
              <CurrencyInput data-testid="input-budget-min" value={f.budget_min} onValueChange={(v) => set("budget_min", v)} placeholder="Min" />
              <CurrencyInput data-testid="input-budget-max" value={f.budget_max} onValueChange={(v) => set("budget_max", v)} placeholder="Max" />
            </div>
            <Err id="budget" />
          </div>
          <div>
            <div className="flex items-center justify-between"><Label>Min. bedrooms</Label><ReqToggle testid="toggle-bedrooms" flexible={f.bedrooms_flexible} onChange={(v) => set("bedrooms_flexible", v)} /></div>
            <Select value={f.min_bedrooms} onValueChange={(v) => set("min_bedrooms", v)}><SelectTrigger data-testid="select-bedrooms" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{["0", "1", "2", "3", "4", "5"].map((t) => <SelectItem key={t} value={t}>{t === "0" ? "Studio / Any" : t}</SelectItem>)}</SelectContent></Select>
          </div>
          <div>
            <div className="flex items-center justify-between"><Label>Parking</Label><ReqToggle testid="toggle-parking" flexible={!f.parking_required} onChange={(v) => set("parking_required", !v)} /></div>
            <Input data-testid="input-parking-min" type="number" min="0" value={f.parking_min} onChange={(e) => set("parking_min", e.target.value)} placeholder="Min slots" className="mt-1.5" />
            <Err id="parking" />
          </div>
          <div>
            <div className="flex items-center justify-between"><Label>Completion</Label><ReqToggle testid="toggle-completion" flexible={f.completion_flexible} onChange={(v) => set("completion_flexible", v)} /></div>
            <Select value={f.completion_pref || NONE} onValueChange={(v) => set("completion_pref", v === NONE ? "" : v)}><SelectTrigger data-testid="select-completion-pref" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={NONE}>— No preference —</SelectItem>{COMPLETION_OPTIONS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
          </div>

          {/* ---- ADVANCED ---- */}
          <div className="md:col-span-2">
            <button type="button" data-testid="toggle-advanced" onClick={() => setShowAdvanced((s) => !s)}
              className="flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary/80 transition-colors">
              <ChevronDown className={`h-4 w-4 transition-transform ${showAdvanced ? "rotate-180" : ""}`} /> Advanced Property Requirements
            </button>
          </div>

          {showAdvanced && (
            <>
              {f.budget_flexible && (
                <div className="md:col-span-2"><Label>Budget tolerance <span className="text-slate-400 font-normal">(% over max still considered, 0–50)</span></Label>
                  <Input data-testid="input-budget-tolerance" type="number" min="0" max="50" value={f.budget_tolerance_pct} onChange={(e) => set("budget_tolerance_pct", e.target.value)} className="mt-1.5" /><Err id="tol" /></div>
              )}
              <div className="md:col-span-2">
                <Label>Alternative / nearby locations <span className="text-slate-400 font-normal">(optional)</span></Label>
                <ServiceAreaPicker testid="buyer-nearby-picker" value={f.nearby} onChange={(v) => set("nearby", v)} />
              </div>
              <div className="md:col-span-2"><Label>Preferred neighborhoods <span className="text-slate-400 font-normal">(optional, comma-separated)</span></Label><Input data-testid="input-neighborhoods" value={f.neighborhoods} onChange={(e) => set("neighborhoods", e.target.value)} placeholder="e.g. BGC, Rockwell" className="mt-1.5" /></div>
              <div>
                <Label>Max. bedrooms <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Select value={f.max_bedrooms || NONE} onValueChange={(v) => set("max_bedrooms", v === NONE ? "" : v)}><SelectTrigger data-testid="select-max-bedrooms" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value={NONE}>— No max —</SelectItem>{["1", "2", "3", "4", "5", "6"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                <Err id="bedrooms" />
              </div>
              <div>
                <Label>Min. bathrooms</Label>
                <Select value={f.min_bathrooms} onValueChange={(v) => set("min_bathrooms", v)}><SelectTrigger data-testid="select-min-bathrooms" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["0", "1", "2", "3", "4"].map((t) => <SelectItem key={t} value={t}>{t === "0" ? "Any" : `${t}+`}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Floor area min (sqm)</Label><Input data-testid="input-floor-min" type="number" min="0" step="any" value={f.floor_area_min} onChange={(e) => set("floor_area_min", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Floor area max (sqm)</Label><Input data-testid="input-floor-max" type="number" min="0" step="any" value={f.floor_area_max} onChange={(e) => set("floor_area_max", e.target.value)} className="mt-1.5" /><Err id="floor" /></div>
              <div><Label>Lot area min (sqm)</Label><Input data-testid="input-lot-min" type="number" min="0" step="any" value={f.lot_area_min} onChange={(e) => set("lot_area_min", e.target.value)} className="mt-1.5" /></div>
              <div><Label>Lot area max (sqm)</Label><Input data-testid="input-lot-max" type="number" min="0" step="any" value={f.lot_area_max} onChange={(e) => set("lot_area_max", e.target.value)} className="mt-1.5" /><Err id="lot" /></div>
              <div className="md:col-span-2">
                <Label>Furnishing</Label>
                <Select value={f.furnishing_pref} onValueChange={(v) => set("furnishing_pref", v)}><SelectTrigger data-testid="select-furnishing" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Any", "Bare", "Semi-Furnished", "Fully Furnished"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
              </div>
              <div className="md:col-span-2">
                <Label>Preferred Features &amp; Amenities</Label>
                <p className="text-sm text-slate-500 mt-1">Select the features and amenities your buyer prefers.</p>
                <div className="flex flex-wrap gap-2 mt-2" data-testid="preferred-amenities">{AMENITIES.map((c) => (
                  <button key={c} type="button" data-testid={`pref-amenity-${c.replace(/[^a-zA-Z]/g, "")}`} onClick={() => toggleFeat("preferred_features", c)} className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${f.preferred_features.includes(c) ? "bg-accent text-accent-foreground border-accent" : "bg-white border-border text-slate-600 hover:border-slate-400"}`}>{c}</button>
                ))}</div>
              </div>
              <div className="md:col-span-2"><Label>Notes</Label><Textarea data-testid="input-notes" value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="e.g. Client prefers a quieter building but close to BGC offices…" className="mt-1.5" /></div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="flex items-center gap-1.5"><Sparkles className="h-4 w-4 text-accent" /> Smart preferences <span className="text-slate-400 font-normal">(AI reads your note)</span></Label>
                  <Button type="button" data-testid="detect-prefs" onClick={detect} disabled={detecting} variant="outline" size="sm" className="rounded-lg">{detecting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Detect from notes"}</Button>
                </div>
                {detected.length > 0 && (
                  <div className="mt-2 border border-accent/30 bg-accent/5 rounded-xl p-3" data-testid="detected-prefs">
                    <p className="text-xs text-slate-500 mb-2">Detected preferences — review before applying:</p>
                    <div className="flex flex-wrap gap-2">
                      {detected.map((d) => <span key={d} className="inline-flex items-center gap-1 text-xs font-medium bg-white border border-border px-2 py-1 rounded-full">{d}<button type="button" onClick={() => setDetected(detected.filter((x) => x !== d))}><X className="h-3 w-3 text-slate-400" /></button></span>)}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button type="button" data-testid="accept-prefs" onClick={() => { setSmartPrefs(detected); setDetected([]); toast.success("Smart preferences applied"); }} size="sm" className="bg-accent text-accent-foreground rounded-lg">Accept</Button>
                      <Button type="button" data-testid="ignore-prefs" onClick={() => setDetected([])} size="sm" variant="ghost" className="rounded-lg">Ignore</Button>
                    </div>
                  </div>
                )}
                {smartPrefs.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid="applied-prefs">
                    {smartPrefs.map((s) => <span key={s} className="inline-flex items-center gap-1 text-xs font-medium bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full"><Check className="h-3 w-3" />{s}</span>)}
                  </div>
                )}
              </div>
            </>
          )}

          <div className="md:col-span-2 flex flex-col-reverse sm:flex-row items-stretch sm:items-center gap-3">
            <Button data-testid="save-draft-buyer" onClick={saveDraft} disabled={savingDraft || saving} variant="outline" className="w-full sm:w-auto h-12 px-6 text-base font-semibold rounded-xl border-[#0F2C59]/30 text-[#0F2C59] hover:bg-[#0F2C59]/5 active:scale-95 transition-transform">{savingDraft ? "Saving…" : "Save Draft"}</Button>
            <Button data-testid="submit-buyer" onClick={submit} disabled={saving || savingDraft} className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 h-12 px-8 text-base font-semibold rounded-xl active:scale-95 transition-transform"><Sparkles className="h-5 w-5 mr-2" /> {saving ? "Saving…" : editId ? "Save changes" : "Find matching properties"}</Button>
            {draftSavedAt && <span data-testid="draft-saved-note" className="text-xs text-slate-500 sm:ml-1">Draft saved · {new Date(draftSavedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>}
          </div>
        </div>
      </Fade>
      <PostPublishDistribution
        open={!!distrib}
        onOpenChange={(o) => { if (!o) { const bid = distrib?.id; setDistrib(null); if (bid) nav(`/buyer/${bid}/matches`); } }}
        kind="buyer_requirement"
        id={distrib?.id}
        onDone={(url, view) => { const bid = distrib?.id; setDistrib(null); nav(view ? `/buyer-need/${bid}` : `/buyer/${bid}/matches`); }}
      />
    </PageShell>
  );
}
